import { motion } from "framer-motion";
import { Disc3, Ear, Sliders, Sparkles } from "lucide-react";

const PHOTO =
  "https://customer-assets-jai6qajn.emergentagent.net/job_adrien-sebert-dj/artifacts/9ght03fs_7B919567-D5F7-4991-83DD-41003BBE130C.png";

const GENRES = ["Années 70", "80", "90", "2000", "Disco", "Funk", "Rock", "Variété française", "Pop", "Latino", "Afro", "Électro", "House", "Hits actuels"];

const PILLARS = [
  { icon: <Ear size={18} />, title: "Lire la piste", text: "Observer, comprendre les générations présentes, anticiper les réactions." },
  { icon: <Disc3 size={18} />, title: "Mixer en direct", text: "Pas de playlist automatique imposée : le bon titre, au bon moment." },
  { icon: <Sliders size={18} />, title: "Construire la progression", text: "Une montée cohérente qui maintient l'énergie jusqu'au bout de la nuit." },
];

const BLOCKS = [
  {
    title: "Plus de 20 ans d'expérience derrière les platines",
    paragraphs: [
      "Au fil des années, j'ai animé et sonorisé plusieurs centaines d'événements, avec des publics allant de quelques dizaines à plusieurs centaines de personnes.",
      "Cette expérience m'a appris une chose essentielle : un bon DJ ne joue pas uniquement ce qu'il aime. Il joue ce qui fonctionne pour les personnes présentes.",
    ],
    genres: true,
  },
  {
    title: "Le matériel ne fait pas le DJ",
    paragraphs: [
      "Une belle sonorisation et un bel éclairage améliorent évidemment l'expérience. Mais aucun matériel ne remplacera jamais l'expérience, la culture musicale, la technique, le timing et la capacité à lire une piste de danse.",
      "C'est cette expertise que vous réservez avant tout lorsque vous faites appel à moi.",
    ],
  },
  {
    title: "Une soirée préparée, mais jamais figée",
    paragraphs: [
      "Avant votre événement, nous définissons ensemble vos goûts, vos incontournables, vos interdits et l'ambiance recherchée. Ensuite, je fais mon métier.",
      "Je construis la soirée en temps réel, morceau après morceau, en fonction de vos invités et de l'énergie présente sur la piste. Parce qu'une grande soirée ne se programme pas entièrement à l'avance.",
    ],
    punch: "Elle se ressent. Elle se construit. Elle se mixe.",
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, delay },
});

export const Manifesto = () => {
  return (
    <section
      id="dj-avant-tout"
      data-testid="manifesto-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5"
    >
      <div className="absolute inset-0 overflow-hidden bg-[radial-gradient(ellipse_at_80%_15%,rgba(212,175,55,0.14),transparent_55%),radial-gradient(ellipse_at_10%_90%,rgba(45,108,255,0.12),transparent_55%)]" />

      <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <motion.div {...fade()} className="relative">
              <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-[radial-gradient(ellipse_at_30%_20%,rgba(255,120,40,0.25),transparent_60%),radial-gradient(ellipse_at_70%_80%,rgba(45,108,255,0.25),transparent_60%)] blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden aspect-[3/4] border border-[#D4AF37]/25 gold-glow">
                <img
                  src={PHOTO}
                  alt="Adrien Sebert – DJ AS derrière les platines, bras ouverts devant la foule"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] mb-1">DJ AS</div>
                  <div className="text-white font-semibold text-xl md:text-2xl leading-tight">L'expérience avant les effets.</div>
                </div>
              </div>
            </motion.div>

            <motion.div {...fade(0.15)} className="mt-6 grid grid-cols-3 gap-3">
              {[["20+", "ans de métier"], ["800+", "prestations"], ["100%", "mix en direct"]].map(([v, l]) => (
                <div key={l} className="glass rounded-xl px-3 py-4 text-center" data-testid={`manifesto-stat-${l.replace(/\s+/g, "-")}`}>
                  <div className="text-xl md:text-2xl font-bold text-[#D4AF37]">{v}</div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400 mt-1">{l}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <motion.div {...fade()}>
            <span className="eyebrow inline-flex items-center gap-2">
              <Sparkles size={13} /> Ma philosophie
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Avant tout, <span className="gold-text-gradient">je suis DJ.</span>
            </h2>
            <p className="mt-6 text-zinc-200 leading-relaxed text-base md:text-lg">
              Avant la lumière, les effets, la vidéo ou l'animation, il y a une chose
              essentielle : <span className="text-white font-medium">la musique.</span>
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              DJ depuis plus de 20 ans, j'ai construit mon expérience derrière les
              platines, en discothèque comme en événementiel, avec une seule priorité :
              faire danser un public et savoir le lire.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Être DJ, ce n'est pas simplement enchaîner des morceaux ou lancer une
              playlist. C'est savoir observer une piste de danse, comprendre les
              générations présentes, anticiper les réactions, choisir le bon titre au
              bon moment et adapter son mix en permanence. Chaque soirée est différente.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Je travaille donc sans playlist automatique imposée de A à Z : je prépare
              évidemment votre univers musical en amont, mais le soir venu, je mixe en
              direct en fonction de votre public.
            </p>
          </motion.div>

          <motion.div {...fade(0.1)} className="mt-9 grid sm:grid-cols-3 gap-3">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`manifesto-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-[#D4AF37]/10 text-[#D4AF37]">{p.icon}</span>
                <div className="mt-3 text-white font-semibold text-sm">{p.title}</div>
                <div className="mt-1 text-xs text-zinc-400 leading-snug">{p.text}</div>
              </div>
            ))}
          </motion.div>

          <motion.p {...fade(0.1)} className="mt-9 text-zinc-300 leading-relaxed border-l-2 border-[#D4AF37] pl-5">
            Mariage, anniversaire, soirée d'entreprise ou événement privé : mon objectif
            reste le même — <span className="text-white">créer une progression musicale cohérente,
            maintenir l'énergie et transformer une soirée en véritable expérience.</span>
          </motion.p>

          <div className="mt-12 space-y-10">
            {BLOCKS.map((b, i) => (
              <motion.div key={b.title} {...fade(0.05 * i)}>
                <h3 className="text-base md:text-lg font-semibold text-white">{b.title}</h3>
                {b.paragraphs.map((t) => (
                  <p key={t} className="mt-3 text-zinc-400 leading-relaxed">{t}</p>
                ))}
                {b.genres && (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="manifesto-genres">
                    {GENRES.map((g) => (
                      <span key={g} className="text-[11px] tracking-[0.14em] uppercase text-zinc-200 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]">
                        {g}
                      </span>
                    ))}
                  </div>
                )}
                {b.punch && (
                  <p className="mt-4 text-lg md:text-xl font-semibold gold-text-gradient">{b.punch}</p>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div {...fade(0.1)} className="mt-14 rounded-3xl glass border border-[#D4AF37]/25 p-7 md:p-9" data-testid="manifesto-closing">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37]">DJ AS — l'expérience avant les effets</div>
            <p className="mt-3 text-white text-lg md:text-2xl font-semibold leading-snug">
              Plus de 20 ans de métier, des centaines de prestations et toujours la même
              passion : voir une piste de danse pleine jusqu'au bout de la nuit.
            </p>
            <a href="#contact" data-testid="manifesto-cta" className="btn-gold rounded-full px-6 py-3 text-sm inline-flex mt-6">
              Parlons de votre soirée
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
